import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  applyDraftToProposal,
  buildChangeRequestPayload,
  convertTimeToReadableFormat,
  getContactNameFromSession,
  hasAnyChanges,
  resolveSubmittedChangeRequest,
  summarizeProductChanges
} from '../../lib/changeRequestDraft';
import { inferRolloverDate, parseYmd } from '../../lib/proposalAcceptance';
import MaykerTimeInput from '../MaykerTimeInput';
import RequestChangesModeHeader from './RequestChangesModeHeader';
import { fontSans, fontSerif, headerButtonStyle, primaryHeaderButtonStyle, textActionStyle, warmHeaderBg } from './requestChangesStyles';
import { formatDisplayDate, formatDisplayDateRange } from '../../utils/displayDate';

const brandTaupe = '#545142';
const inputStyle = {
  width: '100%',
  padding: 10,
  border: `1px solid ${brandTaupe}40`,
  borderRadius: 4,
  fontSize: 14,
  fontFamily: fontSans,
  color: '#000000',
  boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 8,
  color: '#000000',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontFamily: fontSans
};

function formatLongDate(value) {
  return formatDisplayDate(parseYmd(value));
}

function readableTime(value) {
  return convertTimeToReadableFormat(value) || '';
}

function productImageUrl(item) {
  return item?.imageUrl || item?.image || '';
}

function changeKicker(item) {
  if (item.kind === 'new') return 'Added Product';
  if (item.kind === 'removed' || item.removed) return 'Removed Product';
  return 'Quantity Change';
}

function usefulSectionName(name) {
  const section = String(name || '').trim();
  if (!section || /^untitled section$/i.test(section)) return '';
  return section;
}

function changeDetail(item) {
  const section = usefulSectionName(item.sectionName);
  if (item.kind === 'new') {
    if (item.isNewSection) return `Qty. ${item.quantity} · New section: ${item.sectionName}`;
    return section ? `Qty. ${item.quantity} · ${section}` : `Qty. ${item.quantity}`;
  }
  if (item.kind === 'removed' || item.removed) {
    return section ? `Removed from ${section}` : 'Removed';
  }
  return `Quantity: ${item.originalQuantity} → ${item.newQuantity}`;
}

function RecapThumb({ src }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 56,
        height: 56,
        flexShrink: 0,
        backgroundColor: '#ece8df',
        border: '1px solid #e8e0d4',
        overflow: 'hidden'
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
    </div>
  );
}

const CONFIRM_TITLE = 'Ready to submit your changes?';
const CONFIRM_BODY = 'Our team will review your requests, confirm availability, and send an updated proposal for your review.';
const SUCCESS_BODY = 'Your changes have been submitted.';
const GENERATION_ERROR = 'We received your changes, but the updated proposal could not be generated. Our team has been notified. Please stay here — do not submit again.';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function SubmitStatusDialog({
  phase,
  errorMessage,
  onCancel,
  onConfirm,
  onRetry,
  onDismissError
}) {
  if (!phase) return null;
  const busy = phase === 'submitting' || phase === 'success';
  const title = phase === 'success'
    ? 'Changes submitted'
    : phase === 'error'
      ? 'Something went wrong'
      : CONFIRM_TITLE;
  const body = phase === 'submitting'
    ? 'Submitting your changes…'
    : phase === 'success'
      ? SUCCESS_BODY
      : phase === 'error'
        ? errorMessage
        : CONFIRM_BODY;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        fontFamily: fontSans
      }}
      onClick={busy ? undefined : onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-request-submit-title"
        aria-busy={busy}
        style={{
          backgroundColor: '#F7F6F0',
          borderRadius: 12,
          padding: 30,
          maxWidth: 450,
          width: '90%',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
          textAlign: 'center',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes mayker-submit-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        {phase === 'submitting' ? (
          <div
            aria-hidden="true"
            style={{
              width: 22,
              height: 22,
              margin: '0 auto 18px',
              borderRadius: '50%',
              border: '1.5px solid #d8d0c4',
              borderTopColor: '#2C2C2C',
              animation: 'mayker-submit-spin 0.8s linear infinite'
            }}
          />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <img
              src="/mayker_round-stamp-lines-black.png"
              alt=""
              style={{ height: 60, width: 'auto', display: 'block', margin: '0 auto' }}
              onError={(e) => {
                if (!e.target.src.includes('/assets/')) {
                  e.target.src = '/assets/mayker_round-stamp-lines-black.png';
                } else {
                  e.target.style.display = 'none';
                }
              }}
            />
          </div>
        )}
        <div
          id="change-request-submit-title"
          style={{ fontSize: 15, color: '#2C2C2C', marginBottom: 10, lineHeight: 1.5, fontWeight: 400 }}
        >
          {title}
        </div>
        <div style={{ fontSize: 14, color: '#2C2C2C', marginBottom: 30, lineHeight: 1.6 }}>
          {body}
        </div>
        {phase === 'confirm' ? (
          <div style={{ display: 'flex', gap: 15, justifyContent: 'center' }}>
            <button type="button" onClick={onCancel} style={{ ...headerButtonStyle, flex: 1 }}>
              Cancel
            </button>
            <button type="button" onClick={onConfirm} style={{ ...primaryHeaderButtonStyle, flex: 1 }}>
              Submit Changes
            </button>
          </div>
        ) : null}
        {phase === 'submitting' ? (
          <button
            type="button"
            disabled
            aria-label="Submitting changes"
            style={{
              ...primaryHeaderButtonStyle,
              width: '100%',
              opacity: 0.85,
              cursor: 'wait'
            }}
          >
            Submitting Changes…
          </button>
        ) : null}
        {phase === 'error' ? (
          <div style={{ display: 'flex', gap: 15, justifyContent: 'center' }}>
            <button type="button" onClick={onDismissError} style={{ ...headerButtonStyle, flex: 1 }}>
              Stay Here
            </button>
            {onRetry ? (
              <button type="button" onClick={onRetry} style={{ ...primaryHeaderButtonStyle, flex: 1 }}>
                Try Again
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ScheduleChoiceCheck() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2 6.2L4.7 8.8L10 3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function choiceButtonStyle(selected) {
  return {
    flex: 1,
    minHeight: 44,
    padding: '10px 14px',
    borderRadius: 6,
    border: selected ? '1px solid #e8e0d4' : '1px solid #d8d0c4',
    backgroundColor: selected ? warmHeaderBg : '#fff',
    color: '#2C2C2C',
    fontSize: 13,
    fontWeight: 400,
    fontFamily: fontSans,
    cursor: 'pointer',
    textAlign: 'center',
    lineHeight: 1.35,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    boxShadow: 'none',
    outline: 'none'
  };
}

function FieldHint({ children }) {
  return (
    <div style={{ fontSize: 12, color: '#8a847c', fontFamily: fontSans, marginTop: 4, lineHeight: 1.4 }}>
      {children}
    </div>
  );
}

function ScheduleFact({ label, value, note }) {
  return (
    <div>
      <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a847c', fontFamily: fontSans, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#2C2C2C', fontFamily: fontSans, lineHeight: 1.4 }}>
        {value || '—'}
        {note ? <span style={{ color: '#8a847c' }}>{` · ${note}`}</span> : null}
      </div>
    </div>
  );
}

export default function ChangeRequestReviewView({
  proposal,
  sections,
  draft,
  setDateTime,
  setDateTimeFields,
  resetDateTime,
  setMiscNotes,
  removeNewProductRequest,
  undoProductChange,
  eventDatesLabel,
  showAlert,
  showConfirm,
  onBack,
  onExit,
  onRefresh,
  onSubmitted,
  submitChangeRequest,
  getSession,
  modeHeader = null
}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitLocked, setSubmitLocked] = useState(false);
  const [submitPhase, setSubmitPhase] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [canRetrySubmit, setCanRetrySubmit] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const keepScheduleRef = useRef(null);
  const updateScheduleRef = useRef(null);
  const summary = summarizeProductChanges(draft, sections);
  const dt = draft.dateTimeChanges || {};
  const scheduleDateLabel = formatDisplayDateRange(parseYmd(dt.startDate || proposal.startDate), parseYmd(dt.endDate || proposal.endDate))
    || eventDatesLabel || '—';
  const hardSetRollover = inferRolloverDate(
    dt.startDate || proposal.startDate,
    readableTime(dt.deliveryTime || proposal.deliveryTime),
    readableTime(dt.hardSetTime || proposal.hardSetTime)
  );
  const hardOutRollover = inferRolloverDate(
    dt.endDate || proposal.endDate,
    readableTime(dt.strikeTime || proposal.strikeTime),
    readableTime(dt.hardOutTime || proposal.hardOutTime)
  );
  const hardSetDateNote = hardSetRollover && parseYmd(dt.startDate || proposal.startDate) && hardSetRollover !== parseYmd(dt.startDate || proposal.startDate)
    ? formatLongDate(hardSetRollover)
    : '';
  const hardOutDateNote = hardOutRollover && parseYmd(dt.endDate || proposal.endDate) && hardOutRollover !== parseYmd(dt.endDate || proposal.endDate)
    ? formatLongDate(hardOutRollover)
    : '';

  const updateScheduleField = (field, value) => {
    const next = { ...dt, [field]: value };
    if (field === 'hardOutTime' || field === 'strikeTime' || field === 'endDate') {
      next.hardOutDate = inferRolloverDate(
        next.endDate,
        readableTime(next.strikeTime),
        readableTime(next.hardOutTime)
      );
    }
    if (setDateTimeFields) setDateTimeFields(next);
    else {
      setDateTime(field, value);
      if (next.hardOutDate !== dt.hardOutDate) setDateTime('hardOutDate', next.hardOutDate);
    }
  };

  const chooseKeepSchedule = () => {
    setEditingSchedule(false);
    if (resetDateTime) resetDateTime();
  };

  const chooseUpdateSchedule = () => {
    setEditingSchedule(true);
  };

  const onScheduleKeyDown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowDown' && event.key !== 'ArrowLeft' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      chooseUpdateSchedule();
      updateScheduleRef.current?.focus();
      return;
    }
    chooseKeepSchedule();
    keepScheduleRef.current?.focus();
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);
  const hasProductSummary = summary.quantityItems.length > 0 || summary.newItems.length > 0;

  const openSubmitConfirm = async () => {
    if (submitting || submitLocked) return;
    if (!hasAnyChanges(draft, proposal)) {
      await showAlert('Please make at least one change before submitting');
      return;
    }
    setSubmitError('');
    setSubmitPhase('confirm');
  };

  const runSubmit = async () => {
    if (submitting || submitLocked) return;
    setSubmitting(true);
    setSubmitPhase('submitting');
    setSubmitError('');
    setCanRetrySubmit(false);

    const payload = buildChangeRequestPayload({
      draft,
      proposal,
      contactName: getContactNameFromSession(getSession ? getSession() : null)
    });

    try {
      const result = await submitChangeRequest(payload);
      const submitted = resolveSubmittedChangeRequest(result, {
        projectNumber: proposal.projectNumber,
        currentVersion: proposal.version
      });

      if (!submitted.created) {
        console.error('[change-request] saved without a generated proposal version', result);
        setSubmitLocked(true);
        setSubmitPhase('error');
        setSubmitError(GENERATION_ERROR);
        return;
      }

      const optimistic = applyDraftToProposal(proposal, draft, submitted);
      if (onSubmitted) {
        await onSubmitted({
          ...result,
          ...submitted,
          pendingReview: true,
          proposal: optimistic
        });
      } else if (onRefresh) {
        await onRefresh(true, { ...submitted, proposal: optimistic });
      }

      setSubmitPhase('success');
      await sleep(400);
      onExit();
    } catch (err) {
      console.error('[change-request] submit failed', err);
      setSubmitLocked(false);
      setCanRetrySubmit(true);
      setSubmitPhase('error');
      setSubmitError(`Error submitting your changes: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="change-request-wrapper change-request-review" style={{ minHeight: '100vh', backgroundColor: '#fafaf8' }}>
      {modeHeader ? (
        <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
          <RequestChangesModeHeader
            onCancel={modeHeader.onCancel}
            utility={modeHeader.utility || null}
          />
        </div>
      ) : null}
      <div className="change-request-main-container" style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px 56px' }}>
        <style dangerouslySetInnerHTML={{ __html: `
          .change-request-schedule-choice:hover:not([aria-checked="true"]) {
            background-color: #fafaf8 !important;
            border-color: #c4bbb0 !important;
          }
          .change-request-schedule-choice:focus-visible {
            outline: 2px solid #2C2C2C;
            outline-offset: 2px;
          }
          .change-request-back:hover {
            color: #2C2C2C !important;
          }
          .change-request-back:focus-visible,
          .change-request-action-buttons button:focus-visible {
            outline: 2px solid #2C2C2C;
            outline-offset: 3px;
          }
          @media (max-width: 768px) {
            .change-request-project-info { flex-direction: column !important; gap: 16px !important; }
            .change-request-datetime-grid { grid-template-columns: 1fr !important; }
            .change-request-schedule-choices { flex-direction: column !important; }
            .change-request-action-buttons button { width: 100% !important; }
            .change-request-main-container { padding: 24px 16px 48px !important; }
          }
        ` }} />

        {onBack ? (
          <div style={{ marginBottom: 18 }}>
            <button
              type="button"
              className="change-request-back"
              onClick={onBack}
              style={{
                ...textActionStyle,
                fontSize: 12,
                color: '#6b645c',
                textDecoration: 'none'
              }}
            >
              ← Back
            </button>
          </div>
        ) : null}

        <div className="change-request-header-content" style={{ marginBottom: 40, textAlign: 'center' }}>
          <h1 style={{ fontSize: 22, fontWeight: 300, color: '#2C2C2C', margin: '0 0 10px', fontFamily: fontSerif, letterSpacing: '0.02em' }}>
            Review and Submit Changes
          </h1>
          <p style={{ fontSize: 14, color: '#6b645c', fontFamily: fontSans, maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
            Review your requested changes and update any project details before submitting.
          </p>
          <div className="change-request-project-info" style={{ display: 'flex', justifyContent: 'center', gap: 36, flexWrap: 'wrap', marginTop: 28 }}>
            <div>
              <div style={{ fontSize: 11, color: '#8a847c', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontFamily: fontSans }}>Client</div>
              <div style={{ fontSize: 14, color: '#2C2C2C', fontFamily: fontSans }}>{proposal.clientName || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#8a847c', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontFamily: fontSans }}>Venue</div>
              <div style={{ fontSize: 14, color: '#2C2C2C', fontFamily: fontSans }}>{proposal.venueName || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#8a847c', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontFamily: fontSans }}>Project Dates</div>
              <div style={{ fontSize: 14, color: '#2C2C2C', fontFamily: fontSans }}>{eventDatesLabel || '—'}</div>
            </div>
          </div>
        </div>

        <div className="change-request-quantity-section" style={{ marginBottom: 40 }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2C', margin: '0 0 16px', fontFamily: fontSans, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Product Changes
          </h2>
          {!hasProductSummary ? (
            <p style={{ fontSize: 14, color: '#6b645c', fontFamily: fontSans, margin: 0 }}>No product changes requested.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {summary.grouped.map((group) => (
                <div key={group.sectionName}>
                  {usefulSectionName(group.sectionName) ? (
                    <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a847c', fontFamily: fontSans, marginBottom: 10 }}>
                      {group.sectionName}
                    </div>
                  ) : null}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {group.items.map((item) => (
                      <div key={item.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                        <RecapThumb src={productImageUrl(item)} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8a847c', fontFamily: fontSans, marginBottom: 3 }}>
                            {changeKicker(item)}
                          </div>
                          <div style={{ fontSize: 15, color: '#2C2C2C', fontFamily: fontSans, lineHeight: 1.35 }}>
                            {item.productName}
                          </div>
                          <div style={{ fontSize: 13, color: '#6b645c', fontFamily: fontSans, marginTop: 3, lineHeight: 1.4 }}>
                            {changeDetail(item)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (item.kind === 'new') removeNewProductRequest(item.index);
                            else {
                              const [sectionIdx, productIdx] = item.key.split('-').map(Number);
                              undoProductChange(sectionIdx, productIdx);
                            }
                          }}
                          style={{ ...textActionStyle, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0, marginTop: 2 }}
                        >
                          {item.kind === 'new' ? 'Remove' : 'Undo'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="change-request-datetime-section" style={{ marginBottom: 40 }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2C', margin: '0 0 8px', fontFamily: fontSans, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Event Dates & Times
          </h2>
          <p id="change-request-schedule-question" style={{ fontSize: 14, color: '#6b645c', fontFamily: fontSans, margin: '0 0 14px' }}>
            Has your event schedule changed?
          </p>
          <div
            className="change-request-schedule-choices"
            role="radiogroup"
            aria-labelledby="change-request-schedule-question"
            style={{ display: 'flex', gap: 10, marginBottom: 22 }}
          >
            <button
              ref={keepScheduleRef}
              type="button"
              className="change-request-schedule-choice"
              role="radio"
              aria-checked={!editingSchedule}
              tabIndex={editingSchedule ? -1 : 0}
              onClick={chooseKeepSchedule}
              onKeyDown={onScheduleKeyDown}
              style={choiceButtonStyle(!editingSchedule)}
            >
              {!editingSchedule ? <ScheduleChoiceCheck /> : null}
              Keep current schedule
            </button>
            <button
              ref={updateScheduleRef}
              type="button"
              className="change-request-schedule-choice"
              role="radio"
              aria-checked={editingSchedule}
              tabIndex={editingSchedule ? 0 : -1}
              onClick={chooseUpdateSchedule}
              onKeyDown={onScheduleKeyDown}
              style={choiceButtonStyle(editingSchedule)}
            >
              {editingSchedule ? <ScheduleChoiceCheck /> : null}
              Update schedule
            </button>
          </div>

          {!editingSchedule ? (
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a847c', fontFamily: fontSans, marginBottom: 8 }}>
                Current Schedule
              </div>
              <div style={{ fontSize: 15, color: '#2C2C2C', fontFamily: fontSans, marginBottom: 16 }}>{scheduleDateLabel}</div>
              <div className="change-request-datetime-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px 24px' }}>
                <ScheduleFact label="Load-In" value={readableTime(proposal.deliveryTime)} />
                <ScheduleFact label="Hard Set" value={readableTime(proposal.hardSetTime)} note={hardSetDateNote} />
                <ScheduleFact label="Strike" value={readableTime(proposal.strikeTime)} />
                <ScheduleFact label="Hard Out" value={readableTime(proposal.hardOutTime)} note={hardOutDateNote} />
              </div>
            </div>
          ) : (
            <div className="change-request-datetime-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 18 }}>
              <div>
                <label style={labelStyle}>Event Start Date</label>
                <input type="date" className="mayker-field" value={dt.startDate || ''} onChange={(e) => updateScheduleField('startDate', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Event End Date</label>
                <input type="date" className="mayker-field" value={dt.endDate || ''} onChange={(e) => updateScheduleField('endDate', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label htmlFor="change-deliveryTime" style={labelStyle}>Load-In Time</label>
                <MaykerTimeInput
                  id="change-deliveryTime"
                  value={dt.deliveryTime}
                  onChange={(value) => updateScheduleField('deliveryTime', value)}
                  outputFormat="input24"
                  style={inputStyle}
                />
              </div>
              <div>
                <label htmlFor="change-hardSetTime" style={labelStyle}>Hard Set Time</label>
                <MaykerTimeInput
                  id="change-hardSetTime"
                  value={dt.hardSetTime}
                  onChange={(value) => updateScheduleField('hardSetTime', value)}
                  outputFormat="input24"
                  style={inputStyle}
                />
                <FieldHint>
                  When setup must be fully complete.
                  {hardSetDateNote ? ` ${hardSetDateNote}` : ''}
                </FieldHint>
              </div>
              <div>
                <label htmlFor="change-strikeTime" style={labelStyle}>Strike Time</label>
                <MaykerTimeInput
                  id="change-strikeTime"
                  value={dt.strikeTime}
                  onChange={(value) => updateScheduleField('strikeTime', value)}
                  outputFormat="input24"
                  style={inputStyle}
                />
              </div>
              <div>
                <label htmlFor="change-hardOutTime" style={labelStyle}>Hard Out Time</label>
                <MaykerTimeInput
                  id="change-hardOutTime"
                  value={dt.hardOutTime}
                  onChange={(value) => updateScheduleField('hardOutTime', value)}
                  outputFormat="input24"
                  style={inputStyle}
                />
                <FieldHint>
                  When all Mayker product must be removed.
                  {hardOutDateNote ? ` ${hardOutDateNote}` : ''}
                </FieldHint>
              </div>
            </div>
          )}
        </div>

        <div className="change-request-misc-section" style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2C', margin: '0 0 8px', fontFamily: fontSans, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Other Requests
          </h2>
          <p style={{ fontSize: 14, color: '#6b645c', fontFamily: fontSans, margin: '0 0 12px' }}>
            Anything else you'd like us to review?
          </p>
          <textarea
            className="mayker-field"
            value={draft.miscNotes}
            onChange={(e) => setMiscNotes(e.target.value)}
            placeholder="Add any other requests or details here..."
            rows={5}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div className="change-request-action-buttons" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={openSubmitConfirm}
            disabled={submitting || submitLocked}
            style={{
              ...primaryHeaderButtonStyle,
              opacity: submitting || submitLocked ? 0.6 : 1,
              cursor: submitting || submitLocked ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Submitting Changes…' : 'Submit Changes'}
          </button>
        </div>
      </div>
      <SubmitStatusDialog
        phase={submitPhase}
        errorMessage={submitError}
        onCancel={() => {
          if (submitting) return;
          setSubmitPhase(null);
        }}
        onConfirm={runSubmit}
        onRetry={canRetrySubmit ? runSubmit : null}
        onDismissError={() => setSubmitPhase(null)}
      />
    </div>
  );
}
